import type { BackendModule, InitOptions, ReadCallback } from 'i18next';

type LocaleLoaders = Record<string, () => Promise<{ default: Record<string, string> }>>;

/**
 * Бэкенд i18next поверх карты загрузчиков `./locales/*.json` (import.meta.glob
 * в i18n.ts): тянет только запрошенную локаль.
 *
 * Файлы словарей названы в нижнем регистре (`zh-hans.json`, как
 * CoreLangRepository::fileSubtag() на бэке), а i18next канонизирует коды через
 * Intl.getCanonicalLocales: 'zh-hans' → 'zh-Hans'. Поэтому ключ файла строится
 * из кода в нижнем регистре — иначе китайский словарь не находился, и весь
 * интерфейс молча уходил в fallback 'en'.
 */
export const createLocaleBackend = (loaders: LocaleLoaders): BackendModule => ({
	type: 'backend',
	init() {},
	read(lng: string, _ns: string, callback: ReadCallback) {
		const loader = loaders[`./locales/${lng.toLowerCase()}.json`];
		if (!loader) {
			// Неизвестная локаль: отдаём пустой словарь вместо ошибки, чтобы
			// i18next не уходил в ретраи; ключи возьмутся из fallback 'en'.
			callback(null, {});
			return;
		}
		loader().then(
			(mod) => callback(null, mod.default),
			(err) => callback(err as Error, null)
		);
	},
});

/** Опции init без `lng` — общие для приложения и тестов. */
export const I18N_OPTIONS: InitOptions = {
	fallbackLng: 'en',
	// Код языка — в том же нижнем регистре, что window.App.locale после
	// нормализации и имена файлов словарей ('zh-hans', не 'zh-Hans').
	lowerCaseLng: true,
	// Ключи локалей плоские (`"result.history": "..."`) — точка
	// является частью самого ключа, а не разделителем пути. Без этого
	// флага и рантайм, и типы пытаются навигировать `result → history`.
	keySeparator: false,
	nsSeparator: false,
	interpolation: {
		escapeValue: false,
	},
	// Рендер гейтится на i18nReady, Suspense не нужен; без флага
	// useTranslation подвешивал бы компоненты на changeLanguage
	// (переключатель локали в Storybook).
	react: { useSuspense: false },
};
